import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, MessageCircle, Calendar, User as UserIcon, AlertOctagon, PlayCircle, X, Check, Receipt, Copy, ChevronsRight, CheckCircle2 } from 'lucide-react';
import { StatusPill } from '../ui/status-pill';

// State machine: valid forward transitions from any current status.
// Keeps options logical (e.g. "No Show" is only possible before consultation starts).
const ALLOWED_TRANSITIONS = {
  'in-queue':         ['waiting', 'next-in-line', 'no-show'],
  'waiting':          ['next-in-line', 'in-consultation', 'no-show'],
  'next-in-line':     ['in-consultation', 'waiting', 'no-show'],
  'in-consultation':  ['payment-pending', 'completed'],
  'payment-pending':  ['completed', 'in-consultation'],
  'completed':        [],
  'no-show':          ['in-queue']
};

export const PatientSection = ({ title, patients, collapsed, onToggle, expandedRow, onRowClick, onStatusChange }) => {
  if (patients.length === 0) return null;

  const statusKey = title.toLowerCase().replace(/ /g, '-');

  return (
    <>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-5 py-2 transition-colors duration-180 hover:bg-[rgba(28,25,23,0.02)]"
        style={{
          background: 'rgba(28, 25, 23, 0.03)',
          cursor: collapsed !== undefined ? 'pointer' : 'default'
        }}
      >
        <span className="text-overline" style={{ color: getStatusColor(statusKey) }}>
          {title} · {patients.length}
        </span>
        {collapsed !== undefined && (
          collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />
        )}
      </button>

      {!collapsed && patients.map(patient => (
        <PatientRow
          key={patient.id}
          patient={patient}
          isExpanded={expandedRow === patient.id}
          onClick={() => onRowClick(expandedRow === patient.id ? null : patient.id)}
          onStatusChange={onStatusChange}
        />
      ))}
    </>
  );
};

const PatientRow = ({ patient, isExpanded, onClick, onStatusChange }) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const getRowStyle = () => {
    const baseStyle = {
      borderBottom: '1px solid rgba(28, 25, 23, 0.04)',
      transition: 'all 180ms'
    };

    // Emergency rows get a high-priority red treatment that overrides status color
    if (patient.is_emergency && patient.status !== 'completed' && patient.status !== 'no-show') {
      return {
        ...baseStyle,
        borderLeft: '3px solid #B91C1C',
        background: 'rgba(185, 28, 28, 0.04)'
      };
    }

    switch (patient.status) {
      case 'in-consultation':
        return { ...baseStyle, borderLeft: '3px solid var(--color-slate)', background: 'rgba(61, 90, 128, 0.03)' };
      case 'next-in-line':
        return { ...baseStyle, borderLeft: '3px solid #B5612E', background: 'rgba(212, 120, 74, 0.03)' };
      case 'waiting':
        return baseStyle;
      case 'in-queue':
        return { ...baseStyle, opacity: 0.88 };
      case 'payment-pending':
        return { ...baseStyle, borderLeft: '3px solid #F59E0B', background: 'rgba(245, 158, 11, 0.03)' };
      case 'completed':
        return { ...baseStyle, opacity: 0.6 };
      case 'no-show':
        return { ...baseStyle, opacity: 0.5 };
      default:
        return baseStyle;
    }
  };

  return (
    <>
      <div
        className="flex items-center px-5 cursor-pointer"
        style={{
          height: '60px',
          position: 'relative',
          zIndex: showStatusDropdown ? 60 : 'auto',
          ...getRowStyle(),
          ...(isHovered && { transform: 'translateX(1px)', background: 'rgba(212, 120, 74, 0.03)' })
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="font-['DM_Mono'] text-sm font-medium"
          style={{ width: '80px', color: getTokenColor(patient.status) }}
        >
          {patient.token_number}
        </div>

        <div style={{ width: '160px', position: 'relative' }}>
          <div onClick={(e) => { e.stopPropagation(); setShowStatusDropdown(!showStatusDropdown); }}>
            <StatusPill status={patient.status} />
          </div>
          
          {showStatusDropdown && (
            <StatusDropdown
              currentStatus={patient.status}
              onSelect={(newStatus) => {
                onStatusChange(patient.id, newStatus);
                setShowStatusDropdown(false);
              }}
              onClose={() => setShowStatusDropdown(false)}
            />
          )}
        </div>

        <div className="flex-1" style={{ minWidth: '220px' }}>
          <div className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>
            {patient.patient_name}
          </div>
          <div className="font-['Inter'] text-[11px]" style={{ color: '#78716C' }}>
            {patient.chief_complaint || 'No complaint'}
          </div>
        </div>

        <div style={{ width: '120px' }}>
          <EntryPill method={patient.entry_method} />
        </div>

        <div
          className="font-['DM_Mono'] text-sm font-medium text-right"
          style={{ width: '120px', color: getTimerColor(patient) }}
        >
          {getTimerDisplay(patient)}
        </div>

        <div
          className="flex items-center justify-end gap-1.5 transition-opacity duration-180"
          style={{ width: '140px', opacity: isHovered ? 1 : 0 }}
        >
          {getRowActions(patient.status).map((action) => (
            <ActionButton
              key={action.key}
              icon={action.icon}
              tone={action.tone}
              title={action.title}
              onClick={() => action.next ? onStatusChange(patient.id, action.next) : null}
            />
          ))}
        </div>
      </div>

      {isExpanded && (
        <div
          className="px-5 py-5 animate-slide-up"
          style={{
            background: 'rgba(212, 120, 74, 0.03)',
            borderBottom: '1px solid rgba(28, 25, 23, 0.08)'
          }}
        >
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-overline mb-2">PATIENT DETAILS</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-['Inter'] text-sm" style={{ color: '#44403C' }}>
                    {patient.patient_phone}
                  </span>
                  <button className="opacity-60 hover:opacity-100">
                    <Copy size={12} />
                  </button>
                </div>
                <div className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>
                  {patient.chief_complaint}
                </div>
              </div>
            </div>

            <div>
              <div className="text-overline mb-2">RECENT VISITS</div>
              <div className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>
                No previous visits
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {patient.status !== 'in-consultation' && patient.status !== 'completed' && patient.status !== 'payment-pending' && patient.status !== 'no-show' && (
                <button
                  onClick={() => onStatusChange(patient.id, 'in-consultation')}
                  className="h-11 rounded-[var(--radius-lg)] bg-copper-gradient text-white font-['Sora'] font-semibold"
                >
                  Begin Consultation
                </button>
              )}
              {patient.status === 'in-consultation' && (
                <button
                  onClick={() => onStatusChange(patient.id, 'payment-pending')}
                  className="h-11 rounded-[var(--radius-lg)] bg-copper-gradient text-white font-['Sora'] font-semibold"
                >
                  Send to Billing
                </button>
              )}
              {patient.status === 'payment-pending' && (
                <button
                  onClick={() => onStatusChange(patient.id, 'completed')}
                  className="h-11 rounded-[var(--radius-lg)] font-['Sora'] font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #16A34A, #15803D)' }}
                >
                  Mark Paid &amp; Complete
                </button>
              )}
              <button className="h-9 glass-subtle rounded-[var(--radius-md)] font-['Sora'] text-sm font-medium">
                Send WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const StatusDropdown = ({ currentStatus, onSelect, onClose }) => {
  const allowedNext = ALLOWED_TRANSITIONS[currentStatus] || [];

  if (allowedNext.length === 0) {
    return (
      <>
        <div className="fixed inset-0 z-40" onClick={onClose} />
        <div
          className="absolute top-full left-0 mt-1 rounded-[var(--radius-md)] p-3 z-[100] animate-slide-up"
          style={{
            minWidth: '220px',
            background: '#FFFFFF',
            backgroundColor: '#FFFFFF',
            isolation: 'isolate',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: '1px solid rgba(28, 25, 23, 0.12)',
            boxShadow: '0 24px 48px rgba(28, 25, 23, 0.18), 0 0 0 1px rgba(255,255,255,0.5) inset'
          }}
        >
          <span className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>
            No further transitions
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      <div
        className="absolute top-full left-0 mt-1 rounded-[var(--radius-md)] p-2 z-[100] animate-slide-up"
        style={{
          minWidth: '220px',
          background: '#FFFFFF',
          backgroundColor: '#FFFFFF',
          isolation: 'isolate',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: '1px solid rgba(28, 25, 23, 0.12)',
          boxShadow: '0 24px 48px rgba(28, 25, 23, 0.18), 0 0 0 1px rgba(255,255,255,0.5) inset'
        }}
        data-testid="status-dropdown"
      >
        <div className="text-overline px-2 pt-1 pb-2" style={{ color: '#78716C' }}>
          MOVE TO
        </div>
        {allowedNext.map(status => (
          <button
            key={status}
            onClick={() => onSelect(status)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-[var(--radius-sm)] hover:bg-[rgba(61,90,128,0.06)] transition-colors duration-180 text-left"
            data-testid={`status-option-${status}`}
          >
            <StatusPill status={status} />
          </button>
        ))}
      </div>
    </>
  );
};

const EntryPill = ({ method }) => {
  const icons = {
    'Call': Phone,
    'WhatsApp': MessageCircle,
    'Appointment': Calendar,
    'Walk-in': UserIcon,
    'Emergency': AlertOctagon
  };

  const Icon = icons[method] || UserIcon;
  const isEmergency = method === 'Emergency';

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${isEmergency ? '' : 'glass-subtle'}`}
      style={isEmergency ? {
        background: 'rgba(185, 28, 28, 0.08)',
        border: '1px solid rgba(185, 28, 28, 0.20)'
      } : undefined}
    >
      <Icon size={12} strokeWidth={1.75} style={{ color: isEmergency ? '#B91C1C' : 'var(--color-slate)' }} />
      <span className="font-['Sora'] text-[11px] font-medium" style={{ color: isEmergency ? '#B91C1C' : 'var(--color-slate)' }}>
        {method}
      </span>
    </div>
  );
};

const ActionButton = ({ icon: Icon, onClick, tone = 'default', title }) => {
  const tones = {
    default: { color: 'var(--color-slate)', hover: 'rgba(212,120,74,0.08)' },
    primary: { color: '#B5612E', hover: 'rgba(212,120,74,0.12)' },
    success: { color: '#16A34A', hover: 'rgba(22,163,74,0.10)' },
    danger:  { color: '#B91C1C', hover: 'rgba(185,28,28,0.08)' }
  };
  const t = tones[tone] || tones.default;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      className="w-8 h-8 rounded-full glass-subtle flex items-center justify-center transition-all duration-180"
      style={{ background: undefined }}
      onMouseEnter={(e) => { e.currentTarget.style.background = t.hover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
    >
      <Icon size={14} strokeWidth={1.75} style={{ color: t.color }} />
    </button>
  );
};

// Context-aware action buttons per row, driven by the same state machine.
const getRowActions = (status) => {
  switch (status) {
    case 'in-queue':
      return [
        { key: 'arrived', icon: Check, next: 'waiting', tone: 'primary', title: 'Mark arrived' },
        { key: 'noshow',  icon: X,     next: 'no-show', tone: 'danger',  title: 'No show' }
      ];
    case 'waiting':
      return [
        { key: 'next',  icon: ChevronsRight, next: 'next-in-line',    tone: 'primary', title: 'Call next' },
        { key: 'start', icon: PlayCircle,    next: 'in-consultation', tone: 'default', title: 'Start consultation' },
        { key: 'noshow',icon: X,              next: 'no-show',        tone: 'danger',  title: 'No show' }
      ];
    case 'next-in-line':
      return [
        { key: 'start', icon: PlayCircle, next: 'in-consultation', tone: 'primary', title: 'Start consultation' },
        { key: 'noshow',icon: X,           next: 'no-show',        tone: 'danger',  title: 'No show' }
      ];
    case 'in-consultation':
      return [
        { key: 'pay',  icon: Receipt,      next: 'payment-pending', tone: 'primary', title: 'Send to billing' },
        { key: 'done', icon: CheckCircle2, next: 'completed',       tone: 'success', title: 'Complete' }
      ];
    case 'payment-pending':
      return [
        { key: 'done', icon: CheckCircle2, next: 'completed', tone: 'success', title: 'Mark paid & complete' }
      ];
    case 'completed':
      return [];
    case 'no-show':
      return [
        { key: 'requeue', icon: PlayCircle, next: 'in-queue', tone: 'default', title: 'Add back to queue' }
      ];
    default:
      return [];
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'in-consultation': return 'var(--color-slate)';
    case 'next-in-line': return '#B5612E';
    case 'waiting': return 'var(--color-slate)';
    case 'payment-pending': return '#F59E0B';
    default: return '#78716C';
  }
};

const getTokenColor = (status) => {
  switch (status) {
    case 'in-consultation': return 'var(--color-slate)';
    case 'next-in-line': return '#8E4A23';
    case 'waiting': return '#44403C';
    case 'in-queue': return '#78716C';
    default: return '#A8A29E';
  }
};

const getTimerColor = (patient) => {
  const minutes = getWaitMinutes(patient);
  if (minutes > 25) return '#EF4444';
  if (minutes > 10) return '#F59E0B';
  return 'var(--color-slate)';
};

const getWaitMinutes = (patient) => {
  const start = patient.arrived_at || patient.registered_at;
  if (!start) return 0;
  return Math.floor((new Date() - new Date(start)) / 60000);
};

const getTimerDisplay = (patient) => {
  if (patient.status === 'completed' || patient.status === 'no-show') return '—';
  
  const minutes = getWaitMinutes(patient);
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:00`;
  return `${mins}:00`;
};
